import { useCallback, useEffect, useRef, useState } from 'react'
import { motion, useReducedMotion } from 'motion/react'
import { useNavigation } from '@/contexts/NavigationProvider'
import { useGame } from '@/contexts/GameProvider'
import { useProgress } from '@/contexts/ProgressProvider'
import { useCommitRun } from '@/hooks/useCommitRun'
import { useGraceLeaderboard } from '@/hooks/useGraceLeaderboard'
import { getLevel, LEVEL_COUNT } from '@/game/levels'
import { computeScoreBreakdown, nextLevelAdvanceCountdownStep } from '@/game/scoring'
import { achievementById } from '@/game/achievements'
import { hearthBeat, type StoryBeat } from '@/game/storyBeats'
import { GameButton } from '@/components/ui/GameButton'
import { PixelIcon } from '@/components/ui/PixelIcon'
import { ArtImage } from '@/components/ui/ArtImage'
import { AmbientBackdrop } from '@/components/ui/AmbientBackdrop'
import { useGameAudio } from '@/contexts/GameAudioProvider'

const CONFETTI_COLORS = ['#ffb454', '#ffcf5c', '#7de3ff', '#5fd38b', '#e56aff', '#ff9a7a']

interface Shard {
  id: number
  x: number
  drift: number
  size: number
  delay: number
  duration: number
  color: string
  spin: number
}

const SHARDS: Shard[] = Array.from({ length: 28 }, (_, i) => ({
  id: i,
  x: (i * 37 + 11) % 100,
  drift: ((i % 7) - 3) * 26,
  size: 5 + ((i * 13) % 6),
  delay: (i % 9) * 0.06,
  duration: 2.2 + ((i * 7) % 10) / 8,
  color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
  spin: ((i % 2) * 2 - 1) * (240 + ((i * 31) % 240)),
}))

function Confetti() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      {SHARDS.map((s) => (
        <motion.span
          key={s.id}
          className="absolute"
          style={{
            left: `${s.x}%`,
            top: '-4%',
            width: s.size,
            height: s.size * 0.55,
            background: s.color,
            borderRadius: 2,
          }}
          initial={{ y: 0, opacity: 0, rotate: 0 }}
          animate={{ y: '108vh', x: s.drift, opacity: [0, 1, 1, 0.6], rotate: s.spin }}
          transition={{ duration: s.duration, delay: s.delay, ease: [0.2, 0.6, 0.6, 1] }}
        />
      ))}
    </div>
  )
}

function StarRow({ count }: { count: number }) {
  return (
    <div className="flex items-center justify-center gap-3">
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          initial={{ scale: 0.2, rotate: -40, opacity: 0 }}
          animate={{ scale: i < count ? 1 : 0.6, rotate: 0, opacity: i < count ? 1 : 0.3 }}
          transition={{ delay: 0.2 + i * 0.18, type: 'spring', stiffness: 300, damping: 16 }}
          style={{
            color: i < count ? 'var(--coin)' : 'var(--text-faint)',
            filter: i < count ? 'drop-shadow(0 0 10px rgba(255,207,92,0.5))' : 'none',
          }}
        >
          <PixelIcon name="star" size={44} />
        </motion.div>
      ))}
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-[var(--edge)]/30 last:border-0">
      <span className="text-[var(--text-dim)]">{label}</span>
      <span className="font-display font-bold tabular-nums">{value}</span>
    </div>
  )
}

function ScoreRow({ label, value, muted = false }: { label: string; value: number; muted?: boolean }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className={muted ? 'text-[var(--text-faint)]' : 'text-[var(--text-dim)]'}>{label}</span>
      <span className="font-display font-bold tabular-nums">{value > 0 ? `+${value}` : '0'}</span>
    </div>
  )
}

function useCountUp(target: number, durationMs: number, active: boolean): number {
  const [value, setValue] = useState(active ? 0 : target)
  const reduced = useReducedMotion()
  useEffect(() => {
    if (!active || reduced || target <= 0) {
      setValue(target)
      return
    }
    let raf = 0
    const start = performance.now()
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs)
      const eased = 1 - Math.pow(1 - t, 3)
      setValue(Math.round(target * eased))
      if (t < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target, durationMs, active, reduced])
  return value
}

export function LevelCompleteScreen() {
  const { lastRun, lastLevelIndex } = useGame()
  const { go, startLevel } = useNavigation()
  const { save } = useProgress()
  const commit = useCommitRun()
  const { submitGrace } = useGraceLeaderboard()
  const audio = useGameAudio()
  const reduced = useReducedMotion()
  const committed = useRef(false)
  const preAchievements = useRef<string[] | null>(null)
  const preBiomes = useRef<string[] | null>(null)
  const nextTimerRef = useRef(0)
  const [nextCountdown, setNextCountdown] = useState<number | null>(null)

  const level = lastLevelIndex != null ? getLevel(lastLevelIndex) : undefined
  const displayScore = useCountUp(lastRun?.score ?? 0, 900, true)
  const canAdvance = level != null && level.index + 1 < LEVEL_COUNT

  const beginNextCountdown = useCallback(() => {
    if (!level || !canAdvance || nextCountdown != null) return
    setNextCountdown(nextLevelAdvanceCountdownStep(null))
    audio.play('levelup')
  }, [audio, canAdvance, level, nextCountdown])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        if (level && canAdvance) beginNextCountdown()
        else go('map')
      } else if (e.key === 'Escape') {
        go('map')
      } else if (e.key.toLowerCase() === 'r' && level) {
        startLevel(level.index)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [level, canAdvance, beginNextCountdown, startLevel, go])

  useEffect(() => {
    if (!level || nextCountdown == null) return
    nextTimerRef.current = window.setTimeout(() => {
      const next = nextLevelAdvanceCountdownStep(nextCountdown)
      if (next == null) startLevel(level.index + 1)
      else {
        setNextCountdown(next)
        audio.play('confirm')
      }
    }, 700)
    return () => window.clearTimeout(nextTimerRef.current)
  }, [audio, level, nextCountdown, startLevel])

  useEffect(() => {
    if (committed.current || !level || !lastRun) return
    committed.current = true
    preAchievements.current = [...save.meta.achievements]
    preBiomes.current = [...save.progress.unlockedBiomes]
    commit(level, lastRun)
    audio.startMusic('triumph', level.index + 1)
    void submitGrace()
    return () => audio.stopMusic()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level, lastRun, commit, submitGrace, audio])

  if (!level || !lastRun) {
    return (
      <div className="flex h-full items-center justify-center">
        <GameButton icon="home" onClick={() => go('map')}>
          Back to Map
        </GameButton>
      </div>
    )
  }

  const newAchievements = (preAchievements.current
    ? save.meta.achievements.filter((id) => !preAchievements.current!.includes(id))
    : []
  )
    .map(achievementById)
    .filter((a): a is NonNullable<typeof a> => a != null)

  const timeSec = (lastRun.timeMs / 1000).toFixed(1)
  const hasNext = level.index + 1 < LEVEL_COUNT
  const scoreBreakdown = computeScoreBreakdown(level, lastRun, lastRun.stars)

  const newlyUnlocked = preBiomes.current
    ? save.progress.unlockedBiomes.filter((b) => !preBiomes.current!.includes(b))
    : []
  let storyBeat: StoryBeat | null = null
  if (!hasNext) {
    storyBeat = hearthBeat('dungeon')
  } else if (newlyUnlocked.length > 0) {
    storyBeat = hearthBeat(level.biome)
  }

  return (
    <div className="relative flex h-full flex-col items-center justify-center gap-6 px-6 overflow-hidden">
      <AmbientBackdrop />
      {!reduced && lastRun.stars > 0 && <Confetti />}

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="relative w-full max-w-sm">
        <div className="text-center mb-5">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="text-[var(--text-dim)] text-sm"
          >
            Level Complete
          </motion.p>
          <h1 className="font-display text-2xl font-black">{level.name}</h1>
        </div>

        <StarRow count={lastRun.stars} />

        {newAchievements.length > 0 && (
          <div className="mt-4 flex flex-col gap-1.5">
            {newAchievements.map((a, i) => (
              <motion.div
                key={a.id}
                initial={{ opacity: 0, x: -18, scale: 0.95 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                transition={{ delay: 0.7 + i * 0.15, type: 'spring', stiffness: 320, damping: 24 }}
                className="flex items-center gap-2.5 rounded-[var(--r-md)] border border-[var(--coin)]/40 bg-[var(--surface-raised)] px-3 py-2 shadow-emboss"
              >
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[var(--surface-sunken)] text-[var(--coin)]">
                  <PixelIcon name={a.icon} size={16} />
                </span>
                <div className="min-w-0">
                  <div className="text-xs font-black uppercase tracking-wide text-[var(--coin)]">Achievement</div>
                  <div className="text-sm font-display font-bold leading-tight">{a.name}</div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {storyBeat && (
          <motion.div
            initial={{ opacity: 0, y: 14, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.5, type: 'spring', stiffness: 280, damping: 26 }}
            className="mt-5 overflow-hidden rounded-[var(--r-lg)] bg-[var(--surface)] shadow-emboss border border-[var(--ember)]/40"
          >
            <div className="relative aspect-[16/9] w-full overflow-hidden">
              <ArtImage src={storyBeat.image} alt={storyBeat.title} className="h-full w-full" fallbackIcon="star" fallbackColor="var(--ember)" reducedMotion={!!reduced} fit="cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--surface)] via-transparent to-transparent" />
            </div>
            <div className="px-4 py-3">
              <div className="text-[10px] font-black uppercase tracking-wider text-[var(--ember)]">Story</div>
              <div className="font-display text-base font-bold leading-tight">{storyBeat.title}</div>
              <p className="mt-1 text-xs leading-relaxed text-[var(--text-dim)]">{storyBeat.subtitle}</p>
            </div>
          </motion.div>
        )}

        <div className="mt-6 rounded-[var(--r-lg)] bg-[var(--surface)] shadow-emboss border border-[var(--edge)]/40 px-5 py-3">
          <div className="flex items-center justify-between border-b border-[var(--edge)]/30 pb-2">
            <span className="text-[var(--text-dim)]">Score</span>
            <span className="font-display text-2xl font-black tabular-nums text-[var(--coin)]">{displayScore}</span>
          </div>
          <div className="border-b border-[var(--edge)]/30 py-2 text-sm">
            <ScoreRow label="Collectibles" value={scoreBreakdown.coins} muted={scoreBreakdown.coins === 0} />
            <ScoreRow label="Time bonus" value={scoreBreakdown.timeBonus} muted={scoreBreakdown.timeBonus === 0} />
            <ScoreRow label="Route bonus" value={scoreBreakdown.routeBonus} muted={scoreBreakdown.routeBonus === 0} />
            <ScoreRow label="Star bonus" value={scoreBreakdown.starBonus} muted={scoreBreakdown.starBonus === 0} />
          </div>
          <Row label="Slides" value={`${lastRun.slides} / ${level.stepTarget}`} />
          <Row label="Coins" value={`${lastRun.coinsCollected} / ${level.coinCount}`} />
          <Row label="Time" value={`${timeSec}s`} />
          <Row label="Best" value={String(save.progress.levelBest[level.index]?.score ?? lastRun.score)} />
        </div>

        <div className="mt-6 flex flex-col gap-2">
          {hasNext && (
            <GameButton size="lg" icon="play" fullWidth onClick={beginNextCountdown} disabled={nextCountdown != null}>
              {nextCountdown == null ? 'Next Level' : `Next in ${nextCountdown}`}
            </GameButton>
          )}
          <div className="flex gap-2">
            <GameButton variant="secondary" icon="retry" fullWidth onClick={() => startLevel(level.index)}>
              Retry
            </GameButton>
            <GameButton variant="ghost" icon="levelSelect" fullWidth onClick={() => go('map')}>
              Map
            </GameButton>
          </div>
        </div>
      </motion.div>
    </div>
  )
}
